import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { apiService } from '@/services/api';

/**
 * Custom hook for managing room types
 *
 * UPDATED: Now uses apiService with request cancellation
 */
export const useRoomTypes = (API) => {
  const [roomTypes, setRoomTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Fetch room types
   * Now includes request cancellation
   */
  const fetchRoomTypes = useCallback(async () => {
    const controller = new AbortController();

    setLoading(true);
    setError(null);

    try {
      const response = await apiService.get('/admin/room-types', {
        signal: controller.signal
      });
      setRoomTypes(response.data);
    } catch (err) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
        return;
      }

      setError(err);
      toast.error('Failed to fetch room types');
    } finally {
      setLoading(false);
    }

    return () => controller.abort();
  }, []);

  /**
   * Create room type
   */
  const createRoomType = useCallback(async (roomTypeData) => {
    try {
      await apiService.post('/admin/room-types', roomTypeData);
      toast.success('Room type created successfully');
      await fetchRoomTypes();
      return { success: true };
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create room type');
      return { success: false, error: err };
    }
  }, [fetchRoomTypes]);

  /**
   * Update room type
   */
  const updateRoomType = useCallback(async (roomTypeId, roomTypeData) => {
    try {
      await apiService.put(`/admin/room-types/${roomTypeId}`, roomTypeData);
      toast.success('Room type updated successfully');
      await fetchRoomTypes();
      return { success: true };
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update room type');
      return { success: false, error: err };
    }
  }, [fetchRoomTypes]);

  /**
   * Delete room type
   */
  const deleteRoomType = useCallback(async (roomTypeId) => {
    try {
      await apiService.delete(`/admin/room-types/${roomTypeId}`);
      toast.success('Room type deleted successfully');
      await fetchRoomTypes();
      return { success: true };
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete room type');
      return { success: false, error: err };
    }
  }, [fetchRoomTypes]);

  /**
   * Get room type name by ID
   */
  const getRoomTypeName = useCallback((roomTypeId) => {
    const roomType = roomTypes.find(rt => rt.id === roomTypeId);
    return roomType ? roomType.name : 'Unknown';
  }, [roomTypes]);

  /**
   * Initial fetch with cleanup
   */
  useEffect(() => {
    const cleanup = fetchRoomTypes();
    return () => {
      if (cleanup && typeof cleanup === 'function') {
        cleanup();
      }
    };
  }, [fetchRoomTypes]);

  return {
    roomTypes,
    loading,
    error,
    fetchRoomTypes,
    createRoomType,
    updateRoomType,
    deleteRoomType,
    getRoomTypeName,
  };
};
